import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Megaphone } from "lucide-react";
import OverviewTab from "@/components/comms/OverviewTab";
import ContactsTab from "@/components/comms/ContactsTab";
import ListsTab from "@/components/comms/ListsTab";
import SegmentsTab from "@/components/comms/SegmentsTab";
import TemplatesTab from "@/components/comms/TemplatesTab";
import CampaignsTab from "@/components/comms/CampaignsTab";
import JourneysTab from "@/components/comms/JourneysTab";
import WarmupTab from "@/components/comms/WarmupTab";
import AutomationsTab from "@/components/comms/AutomationsTab";
import SettingsTab from "@/components/comms/SettingsTab";
import ContactTimelineSheet, { type TimelineTarget } from "@/components/comms/ContactTimelineSheet";
import { CommsPausedBanner } from "@/components/comms/GuardPanel";

const TABS = ["overview", "contacts", "lists", "segments", "templates", "campaigns", "journeys", "warmup", "automations", "settings"];

function readParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    const tab = p.get("tab") || "overview";
    const target: TimelineTarget | null = p.get("user") ? { userId: p.get("user")! } : p.get("contact") ? { contactId: p.get("contact")! } : p.get("email") ? { email: p.get("email")! } : null;
    return { tab: TABS.includes(tab) ? tab : "overview", q: p.get("q") || "", target };
  } catch { return { tab: "overview", q: "", target: null }; }
}

export default function AdminComms() {
  const initial = readParams();
  const [tab, setTab] = useState(initial.tab);
  const [timeline, setTimeline] = useState<TimelineTarget | null>(initial.target);
  const openHistory = (contactId: string) => setTimeline({ contactId });

  return (
    <div className="space-y-6">
      <CommsPausedBanner />

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-primary" /> Comms
        </h1>
        <p className="text-muted-foreground text-sm max-w-3xl">
          EduFocal's own email, WhatsApp and SMS console, in place of Brevo. Build a segment from what we know about each child, or a list of leads,
          write a template, send it as a campaign, or let a journey or automation send it for you. Everything respects quiet hours, the daily sending
          cap and a per-contact frequency cap, and marketing never reaches a student.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto justify-start gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-lists">Lists & leads</TabsTrigger>
          <TabsTrigger value="segments" data-testid="tab-segments">Segments</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="journeys" data-testid="tab-journeys">Journeys</TabsTrigger>
          <TabsTrigger value="warmup" data-testid="tab-warmup">Warm-up</TabsTrigger>
          <TabsTrigger value="automations" data-testid="tab-automations">Automations</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-0"><OverviewTab /></TabsContent>
        <TabsContent value="contacts" className="space-y-6 mt-0"><ContactsTab initialQuery={initial.q} onOpenHistory={openHistory} /></TabsContent>
        <TabsContent value="lists" className="space-y-6 mt-0"><ListsTab onOpenHistory={openHistory} /></TabsContent>
        <TabsContent value="segments" className="space-y-6 mt-0"><SegmentsTab /></TabsContent>
        <TabsContent value="templates" className="space-y-6 mt-0"><TemplatesTab /></TabsContent>
        <TabsContent value="campaigns" className="space-y-6 mt-0"><CampaignsTab /></TabsContent>
        <TabsContent value="journeys" className="space-y-6 mt-0"><JourneysTab /></TabsContent>
        <TabsContent value="warmup" className="space-y-6 mt-0"><WarmupTab /></TabsContent>
        <TabsContent value="automations" className="space-y-6 mt-0"><AutomationsTab /></TabsContent>
        <TabsContent value="settings" className="space-y-6 mt-0"><SettingsTab /></TabsContent>
      </Tabs>

      <ContactTimelineSheet target={timeline} onClose={() => setTimeline(null)} />
    </div>
  );
}
